import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { AgentPerformanceRow, BusinessFilter } from "@/models/underwriting";
import { CollapsibleSection } from "./CollapsibleSection";
import { underwritingService } from "@/services/UnderwritingService";
import { useApi } from "@/hooks/useApi";
import { Spinner } from "@/components/ui/spinner";
import { businessToTeamChannel } from "@/helpers/underwritingDashboard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type StatKey = "approved" | "declined" | "alternative" | "moreInfo" | "totalHandled";

const STAT_COLS: { key: StatKey; label: string; color: string }[] = [
  { key: "approved", label: "Approved", color: "text-foreground" },
  { key: "declined", label: "Declined", color: "text-foreground" },
  { key: "alternative", label: "Alternative", color: "text-foreground" },
  { key: "moreInfo", label: "More Info", color: "text-foreground" },
  { key: "totalHandled", label: "Total", color: "text-foreground font-semibold" },
];

const TYPE_OPTIONS = ["All Types", "Lender Override", "Standard"] as const;
type TypeOption = (typeof TYPE_OPTIONS)[number];

function statValue(agent: AgentPerformanceRow, key: StatKey): number {
  return (agent[key] as number | undefined) ?? 0;
}

interface TeamPerformanceSectionProps {
  start: string;
  end: string;
  business: BusinessFilter;
  refreshKey?: number;
  defaultOpen?: boolean;
}

export function TeamPerformanceSection({ start, end, business, refreshKey, defaultOpen }: TeamPerformanceSectionProps) {
  const [typeFilter, setTypeFilter] = useState<TypeOption>("All Types");

  const requestType = typeFilter === "All Types" ? undefined : typeFilter;

  // Fetch every channel for the period so the Business filter can be applied
  // client-side without an extra request.
  const result = useApi(
    (signal) =>
      underwritingService.getTeamPerformance(
        `${start}T00:00:00`,
        `${end}T23:59:59`,
        undefined,
        requestType,
        signal
      ),
    [start, end, requestType, refreshKey]
  );

  const teamChannel = businessToTeamChannel(business);

  const agents = useMemo(() => {
    const rows = result.data ?? [];
    return teamChannel ? rows.filter((a) => a.channel === teamChannel) : rows;
  }, [result.data, teamChannel]);

  const totals = useMemo(() => {
    return STAT_COLS.reduce<Record<StatKey, number>>(
      (acc, { key }) => {
        acc[key] = agents.reduce((sum, a) => sum + statValue(a, key), 0);
        return acc;
      },
      { approved: 0, declined: 0, alternative: 0, moreInfo: 0, totalHandled: 0 }
    );
  }, [agents]);

  const controls = result.error ? null : (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted">
          Type: {typeFilter}
          <ChevronDown className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {TYPE_OPTIONS.map((opt) => (
          <DropdownMenuItem key={opt} onClick={() => setTypeFilter(opt)}>
            {opt}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <CollapsibleSection title="Team Performance" headerSlot={controls} defaultOpen={defaultOpen}>
      {result.loading ? (
        <div className="flex h-24 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-2.5 text-left text-[12px] font-semibold text-muted-foreground">
                  Agent
                </th>
                {STAT_COLS.map(({ label }) => (
                  <th
                    key={label}
                    className="px-4 py-2.5 text-right text-[12px] font-semibold text-muted-foreground"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent.agentName} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">{agent.agentName}</td>
                  {STAT_COLS.map(({ key, color }) => (
                    <td key={key} className={cn("px-4 py-3 text-right tabular-nums", color)}>
                      {statValue(agent, key)}
                    </td>
                  ))}
                </tr>
              ))}
              {agents.length === 0 && (
                <tr>
                  <td
                    colSpan={STAT_COLS.length + 1}
                    className={cn(
                      "px-4 py-6 text-center text-[11px]",
                      result.error ? "text-rose-600" : "text-muted-foreground"
                    )}
                  >
                    {result.error ?? "No agents found for selected filters."}
                  </td>
                </tr>
              )}
            </tbody>
            {agents.length > 0 && (
              <tfoot>
                <tr className="border-t border-border">
                  <td className="px-4 py-3 text-[12px] font-semibold text-foreground bg-slate-100">Total</td>
                  {STAT_COLS.map(({ key }) => (
                    <td key={key} className="px-4 py-3 text-right text-[12px] font-semibold tabular-nums text-foreground bg-slate-100">
                      {totals[key]}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
}
